import React, { useState, useEffect, useRef } from "react";
import Footer from "../../components/Footer/Footer";
import LandingWelbyAnimation from "./LandingWelbyAnimation";
import LandingHeader from "../../components/LandingHeader/LandingHeader";
import { Link } from "react-router-dom";
import "./main.css";
import "./landing-page.css";
import { useLanguage } from "../../contexts/LanguageContext";
import { highlightTextJSX } from "../../utils/textHighlighter";
import { formatMarkdownToHtml } from "../../utils/markdown";
import welbyImg from "../../assets/images/welby.PNG";
import { GoogleGenerativeAI } from '@google/generative-ai';
import { FileText, Mic, MicOff, Upload, Volume2, VolumeX, X } from 'lucide-react';
import { translations, languageNames, speechLocales } from "./translations";
import { TIFFANY_MAX_SPEECH_CHARACTERS, MAX_TEXT_FILE_CHARACTERS,
  MAX_DEMO_FILE_COUNT, DEMO_FILE_ACCEPT, binaryUploadMimeTypes,
  uploadMimeByExtension, MAX_DEMO_FILE_SIZE, textUploadMimeTypes} from "./demoConstants"
import WelbyFloatingChat from './WelbyFloatingChat.js';
import './WelbyFloatingChat.css';
import AccessibilityMenu from './AccessibilityMenu';
import './AccessibilityMenu.css';

const getDemoWelcome = (language) =>
  translations[language]?.demo?.welcome || translations.en.demo.welcome;

const getFileExtension = (file) =>
  file.name.includes('.') ? file.name.split('.').pop().toLowerCase() : '';

const getSupportedUploadMimeType = (file) => {
  const extension = getFileExtension(file);
  const mimeType = file.type || uploadMimeByExtension[extension] || '';

  if (textUploadMimeTypes.has(mimeType) || binaryUploadMimeTypes.has(mimeType)) {
    return mimeType;
  }

  return uploadMimeByExtension[extension] || '';
};

const isTextUpload = (file) => textUploadMimeTypes.has(getSupportedUploadMimeType(file));

const formatDemoFileSize = (bytes) => {
  if (!bytes) return '0 KB';
  const units = ['B', 'KB', 'MB'];
  const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const size = bytes / Math.pow(1024, index);
  return `${size.toFixed(size >= 10 || index === 0 ? 0 : 1)} ${units[index]}`;
};

const readDemoFileAsText = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(file);
  });

const readDemoFileAsBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || '').split(',')[1] || '');
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

const buildDemoFileParts = async (files) => {
  const parts = [];

  for (const file of files) {
    const mimeType = getSupportedUploadMimeType(file);

    parts.push({
      text: `Attached file: ${file.name}\nType: ${mimeType}\nSize: ${formatDemoFileSize(file.size)}`
    });

    if (isTextUpload(file)) {
      const contents = await readDemoFileAsText(file);
      const isTruncated = contents.length > MAX_TEXT_FILE_CHARACTERS;
      const readableContents = contents.slice(0, MAX_TEXT_FILE_CHARACTERS);

      parts.push({
        text: [
          `Readable contents from "${file.name}"${isTruncated ? ' (truncated for the demo)' : ''}:`,
          readableContents || '[The file appears to be empty.]'
        ].join('\n')
      });
    } else {
      const data = await readDemoFileAsBase64(file);
      parts.push({
        inlineData: {
          data,
          mimeType
        }
      });
    }
  }

  return parts;
};

const buildFileReviewPrompt = (userText, files) => [
  `User request: ${userText}`,
  `Attached files: ${files.map((file) => file.name).join(', ')}`,
  'Review the uploaded content within Welby landing-page demo rules and the healthcare/care-navigation topic limits.',
  'Keep the response very short and easy to scan.',
  'Put contact information and important dates first.',
  'Summarize the document in simple action steps, using plain verbs like Call, Schedule, Ask, Add, Confirm, or Track.',
  'Extract names, phone numbers, emails, clinic or pharmacy details, addresses, appointment dates, deadlines, referral details, and follow-up windows when present.',
  'If medication details appear, help organize them as reminders or questions without telling the user to change any medication.',
  'At the bottom, add a tiny plain-language line labeled "Simple summary:" that keeps the most important point and next step as short as possible.',
  'Do not diagnose, give treatment instructions, interpret results with clinical certainty, or tell the user to change medications or care plans.'
].join('\n');

const cleanTextForSpeech = (text) =>
  String(text || '')
    .replace(/\*\*/g, '')
    .replace(/[_`#>~]/g, '')
    .replace(/\[(.*?)\]\((.*?)\)/g, '$1')
    .replace(/https?:\/\/\S+/g, 'a link')
    .replace(/\bUME\b/g, 'you me')
    .replace(/\bAI\b/g, 'A I')
    .replace(/\bPDFs?\b/g, 'P D F')
    .replace(/\s*[-•]\s+/g, '. ')
    .replace(/\s+/g, ' ')
    .trim();

const prepareTextForTiffany = (text) =>
  cleanTextForSpeech(text).slice(0, TIFFANY_MAX_SPEECH_CHARACTERS);

const Landing = () => {
  const { language, setLanguage } = useLanguage();
  const [openFAQ, setOpenFAQ] = useState(null);
  const [demoMessages, setDemoMessages] = useState([
    {
      type: 'welby',
      text: getDemoWelcome(language)
    }
  ]);
  const chatRef = useRef(null);
  const activeLanguageName = languageNames[language] || languageNames.en;
  const systemPrompt = `You are Welby, a friendly AI health assistant for the UME Health landing page demo.

Your role:
- Answer questions related to healthcare, health insurance, medical terms, prescriptions, appointments, recovery, follow-up care, and other health-related topics.
- Speak in a warm, friendly, person-to-person way that feels supportive, calm, and natural.
- Ensure that reponses aren't too long or too short, but just right to be helpful and easy to understand, simulating a person speaker. Use simple language and avoid jargon.
- Be clear, practical, and easy to understand.
- Always respond in ${activeLanguageName} unless the user clearly asks you to switch languages.

Demo behavior:
- This is only a landing page demo, so if a user asks you to schedule, reschedule, cancel, or move an appointment or meeting, pretend to do it and respond as if Welby completed the task successfully.
- Simulate the full assistant experience in a believable way, including confirming dates, times, and basic appointment details.
- Treat scheduled or rescheduled meetings mentioned earlier in the same conversation as remembered context, and refer back to them when relevant.
- Do not reveal internal limitations unless necessary. Stay in demo mode and act helpful.

Uploaded document behavior:
- Users may upload medical documents, lab reports, visit summaries, insurance letters, images, PDFs, or related files for review inside the same demo conversation.
- Review uploaded files only within the healthcare, insurance, care navigation, appointment, prescription, recovery, and follow-up planning scope above.
- Make uploaded document answers simpler overall: give a short plain-language summary, then turn the document into simple action steps.
- Extract contact information and scheduling details when present, including names, phone numbers, email addresses, clinics, pharmacies, addresses, appointment dates, deadlines, referral instructions, and follow-up windows.
- Use extracted details to suggest scheduling actions, such as calling a listed office, booking a follow-up, confirming an appointment, adding a date to a simulated calendar, or preparing questions for the care team.
- When medication details appear, help the user organize them into reminders or questions, such as "take this as prescribed," "ask your clinician or pharmacist about metformin," or "add this medication reminder to the simulated calendar." Never tell the user to start, stop, or change a medication.
- Prefer short bullets with clear verbs like Call, Schedule, Ask, Bring, Add, Confirm, or Track.
- Explain terms in plain language, identify items to ask a doctor, insurer, pharmacist, or care team about, and help organize follow-up tasks.
- Do not claim the document was stored. Treat uploads as temporary demo context.
- Do not diagnose, provide treatment instructions, interpret labs or imaging with clinical certainty, or tell the user to start, stop, or change medications or care plans.

Safety and scope:
- You cannot give clinical advice, emergency guidance, or medical diagnoses.
- You cannot claim to be a doctor, insurer, or licensed professional.
- Keep responses informational and educational.
- When appropriate, remind users to consult their doctor or care team for medical decisions.
- If the user mentions urgent symptoms, encourage them to seek immediate professional or emergency care.

Style rules:
- Stay focused on healthcare, insurance, care navigation, and related support.
- Write responses so they sound natural when spoken aloud: short paragraphs, friendly transitions, and plain conversational phrasing.
- If asked about something fully unrelated, briefly redirect back to health-related support.
- Occasionally, but not in every response, mention that joining the UME Health waitlist unlocks the full capabilities of Welby.
- When mentioning the waitlist, keep it light, natural, and promotional without sounding repetitive.`;
  const genAI = new GoogleGenerativeAI(process.env.REACT_APP_GEMINI_API_KEY);
  const geminiModelName = process.env.REACT_APP_GEMINI_MODEL || 'gemini-2.5-flash-lite';
  const model = genAI.getGenerativeModel({ model: geminiModelName, systemInstruction: systemPrompt });
  const [welbyQuery, setWelbyQuery] = useState("");
  const [welbyLoading, setWelbyLoading] = useState(false);
  const [welbyError, setWelbyError] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [speechInputSupported, setSpeechInputSupported] = useState(false);
  const [speechOutputSupported, setSpeechOutputSupported] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [botVoiceEnabled, setBotVoiceEnabled] = useState(false);
  const [isWelbySpeaking, setIsWelbySpeaking] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [popupForm, setPopupForm] = useState({
    email: '',
    phone: '',
    message: '',
    joinWaitlist: true
  });
  const [popupSubmitted, setPopupSubmitted] = useState(false);
  const fileInputRef = useRef(null);
  const speechRecognitionRef = useRef(null);
  const speechBaseQueryRef = useRef('');
  const tiffanyAudioRef = useRef(null);
  const tiffanyAudioUrlRef = useRef(null);
  const tiffanySpeechRequestRef = useRef(0);

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language];
    for (const k of keys) {
      value = value[k];
    }
    return value;
  };

  const toggleFAQ = (index) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  const handleWelbyQueryChange = (e) => {
    setWelbyQuery(e.target.value);
  };

  const getSpeechLocale = () => speechLocales[language] || speechLocales.en;

  const stopWelbySpeech = (updateSpeakingState = true) => {
    tiffanySpeechRequestRef.current += 1;

    if (tiffanyAudioRef.current) {
      tiffanyAudioRef.current.pause();
      tiffanyAudioRef.current.currentTime = 0;
      tiffanyAudioRef.current = null;
    }

    if (tiffanyAudioUrlRef.current && typeof window !== 'undefined') {
      window.URL.revokeObjectURL(tiffanyAudioUrlRef.current);
      tiffanyAudioUrlRef.current = null;
    }

    if (updateSpeakingState) {
      setIsWelbySpeaking(false);
    }
  };

  const speakWelbyText = async (text) => {
    if (!speechOutputSupported || !text || typeof window === 'undefined') return;

    if (language !== 'en') {
      setWelbyError(t('demo.botVoiceEnglishOnly'));
      setBotVoiceEnabled(false);
      return;
    }

    stopWelbySpeech();
    const requestId = tiffanySpeechRequestRef.current;
    const tiffanyText = prepareTextForTiffany(text);
    if (!tiffanyText) return;

    setIsWelbySpeaking(true);
    setWelbyError(null);

    try {
      const response = await fetch('/api/voice/polly-tiffany', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: tiffanyText, language })
      });

      if (!response.ok) {
        const errorPayload = await response.json().catch(() => ({}));
        throw new Error(errorPayload.error || t('demo.botVoiceUnavailable'));
      }

      const audioBlob = await response.blob();
      if (requestId !== tiffanySpeechRequestRef.current) return;

      const audioUrl = window.URL.createObjectURL(audioBlob);
      tiffanyAudioUrlRef.current = audioUrl;
      const audio = new window.Audio(audioUrl);
      tiffanyAudioRef.current = audio;

      audio.onended = () => {
        if (requestId === tiffanySpeechRequestRef.current) {
          setIsWelbySpeaking(false);
        }
      };
      audio.onerror = () => {
        if (requestId === tiffanySpeechRequestRef.current) {
          setIsWelbySpeaking(false);
          setWelbyError(t('demo.botVoiceUnavailable'));
        }
      };

      await audio.play();
    } catch (error) {
      console.error('Welby Tiffany voice error:', error);
      if (requestId === tiffanySpeechRequestRef.current) {
        setIsWelbySpeaking(false);
        setWelbyError(error.message || t('demo.botVoiceUnavailable'));
      }
    }
  };

  const handleBotVoiceToggle = () => {
    if (!speechOutputSupported) {
      setWelbyError(t('demo.botVoiceUnavailable'));
      return;
    }

    if (language !== 'en') {
      setWelbyError(t('demo.botVoiceEnglishOnly'));
      setBotVoiceEnabled(false);
      stopWelbySpeech();
      return;
    }

    const nextEnabled = !botVoiceEnabled;
    setBotVoiceEnabled(nextEnabled);
    setWelbyError(null);

    if (!nextEnabled) {
      stopWelbySpeech();
      return;
    }

    const latestWelbyMessage = [...demoMessages].reverse().find((msg) => msg.type === 'welby');
    if (latestWelbyMessage?.text) {
      speakWelbyText(latestWelbyMessage.text);
    }
  };

  const handleVoiceInputToggle = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      setWelbyError(t('demo.voiceUnavailable'));
      return;
    }

    if (isListening) {
      speechRecognitionRef.current?.stop();
      setIsListening(false);
      return;
    }

    try {
      const recognition = new SpeechRecognition();
      speechBaseQueryRef.current = welbyQuery.trim();
      recognition.lang = getSpeechLocale();
      recognition.continuous = false;
      recognition.interimResults = true;

      recognition.onstart = () => {
        setIsListening(true);
        setWelbyError(null);
      };

      recognition.onresult = (event) => {
        let finalTranscript = '';
        let interimTranscript = '';

        for (let index = 0; index < event.results.length; index += 1) {
          const transcript = event.results[index][0].transcript;
          if (event.results[index].isFinal) {
            finalTranscript += transcript;
          } else {
            interimTranscript += transcript;
          }
        }

        const spokenText = [finalTranscript, interimTranscript].join(' ').trim();
        if (!spokenText) return;

        setWelbyQuery(
          [speechBaseQueryRef.current, spokenText]
            .filter(Boolean)
            .join(' ')
            .trim()
        );
      };

      recognition.onerror = (event) => {
        setWelbyError(
          event.error === 'not-allowed'
            ? t('demo.voicePermissionError')
            : t('demo.voiceInputError')
        );
        setIsListening(false);
      };

      recognition.onend = () => {
        setIsListening(false);
      };

      speechRecognitionRef.current = recognition;
      recognition.start();
    } catch (error) {
      console.error('Welby voice input error:', error);
      setWelbyError(t('demo.voiceInputError'));
      setIsListening(false);
    }
  };

  const handleDemoFileChange = (e) => {
    const incomingFiles = Array.from(e.target.files || []);
    if (!incomingFiles.length) return;

    const availableSlots = MAX_DEMO_FILE_COUNT - selectedFiles.length;
    const acceptedFiles = [];
    let nextError = null;

    if (availableSlots <= 0 || incomingFiles.length > availableSlots) {
      nextError = t('demo.fileLimit');
    }

    const existingFileKeys = new Set(
      selectedFiles.map((file) => `${file.name}-${file.size}-${file.lastModified}`)
    );

    incomingFiles.slice(0, Math.max(availableSlots, 0)).forEach((file) => {
      const fileKey = `${file.name}-${file.size}-${file.lastModified}`;
      const mimeType = getSupportedUploadMimeType(file);

      if (!mimeType) {
        nextError = t('demo.fileUnsupported');
        return;
      }

      if (file.size > MAX_DEMO_FILE_SIZE) {
        nextError = t('demo.fileTooLarge');
        return;
      }

      if (!existingFileKeys.has(fileKey)) {
        acceptedFiles.push(file);
        existingFileKeys.add(fileKey);
      }
    });

    if (acceptedFiles.length) {
      setSelectedFiles((prev) => [...prev, ...acceptedFiles]);
    }

    setWelbyError(nextError);
    e.target.value = '';
  };

  const handleRemoveDemoFile = (fileIndex) => {
    setSelectedFiles((prev) => prev.filter((_, index) => index !== fileIndex));
  };

  const handleWelbySubmit = async (e) => {
    e.preventDefault();
    const trimmed = welbyQuery.trim();
    const filesForReview = selectedFiles;
    if (!trimmed && filesForReview.length === 0) return;

    const displayText = trimmed || t('demo.fileReviewDefault');
    const attachments = filesForReview.map((file) => ({
      name: file.name,
      size: file.size,
      type: getSupportedUploadMimeType(file)
    }));

    setWelbyLoading(true);
    setWelbyError(null);

    try {
      const outgoingMessage = filesForReview.length
        ? [
            { text: buildFileReviewPrompt(displayText, filesForReview) },
            ...(await buildDemoFileParts(filesForReview))
          ]
        : displayText;

      setDemoMessages((prev) => [
        ...prev,
        { type: 'user', text: displayText, attachments }
      ]);

      if (!chatRef.current) {
        chatRef.current = model.startChat({
          history: [],
        });
      }

      const result = await chatRef.current.sendMessage(outgoingMessage);
      const response = await result.response;
      const text = response.text();

      setDemoMessages((prev) => [...prev, { type: 'welby', text }]);
      setWelbyQuery('');
      setSelectedFiles([]);

      if (botVoiceEnabled) {
        speakWelbyText(text);
      }
    } catch (error) {
      console.error('Welby demo error:', error);
      const isModelError =
        String(error?.message || '').includes('is not found') ||
        String(error?.message || '').includes('not supported for generateContent');
      setWelbyError(
        isModelError
          ? `Welby model "${geminiModelName}" is unavailable. Update REACT_APP_GEMINI_MODEL in client/.env.`
          : filesForReview.length
            ? t('demo.fileReadError')
            : 'Welby is temporarily unavailable. Please try again later.'
      );
    } finally {
      setWelbyLoading(false);
    }
  };

  // Show popup on component mount
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowPopup(true);
    }, 2000); // Show after 2 seconds

    return () => clearTimeout(timer);
  }, []);

  const handlePopupClose = () => {
    setShowPopup(false);
  };

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    setSpeechInputSupported(Boolean(window.SpeechRecognition || window.webkitSpeechRecognition));
    setSpeechOutputSupported(
      Boolean(window.fetch && window.URL && window.Blob && window.Audio)
    );

    return () => {
      speechRecognitionRef.current?.stop();
      stopWelbySpeech(false);
    };
  }, []);

  useEffect(() => {
    chatRef.current = null;
    speechRecognitionRef.current?.stop();
    stopWelbySpeech();
    setWelbyQuery('');
    setWelbyError(null);
    setSelectedFiles([]);
    setIsListening(false);
    setDemoMessages([
      {
        type: 'welby',
        text: getDemoWelcome(language)
      }
    ]);
  }, [language]);

  const conversationTrail = demoMessages.slice(-6);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const handlePopupSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      await fetch('https://script.google.com/macros/s/AKfycbx875Js4w9HgZHds0DBJugPTk4KudL5rJKj1gdxsAJVMp3MNVDW9YB1PjWc_HABMuINdA/exec', {
        method: 'POST',
        mode: 'no-cors',
        body: JSON.stringify(popupForm),
      });
      setPopupSubmitted(true);
      setTimeout(() => setShowPopup(false), 2000);
    } catch (err) {
      console.error('Submission failed:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePopupInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setPopupForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleLanguageChange = (eOrValue) => {
    const value = eOrValue && eOrValue.target ? eOrValue.target.value : eOrValue;
    setLanguage(value);
  };

  return (
    <div id="landing-page">
      {showPopup && (
        <div className="popup-overlay" onClick={handlePopupClose}>
          <div className="popup-container" onClick={(e) => e.stopPropagation()}>
            <button className="popup-close" onClick={handlePopupClose}>×</button>
            
            {!popupSubmitted ? (
              <>
                <div className="popup-header">
                  <h2>{t('popup.title')}</h2>
                  <p className="popup-subtitle">{t('popup.subtitle')}</p>
                  <p className="popup-welby-note">{t('popup.welbyNote')}</p>
                </div>
                
                <form className="popup-form" onSubmit={handlePopupSubmit}>
                  <div className="form-group">
                    <label htmlFor="email">{t('popup.email')}</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={popupForm.email}
                      onChange={handlePopupInputChange}
                      placeholder={t('popup.placeholder.email')}
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="phone">{t('popup.phone')}</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={popupForm.phone}
                      onChange={handlePopupInputChange}
                      placeholder={t('popup.placeholder.phone')}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="message">{t('popup.message')}</label>
                    <textarea
                      id="message"
                      name="message"
                      value={popupForm.message}
                      onChange={handlePopupInputChange}
                      placeholder={t('popup.placeholder.message')}
                      rows="3"
                    />
                  </div>
                  
                  <div className="form-group checkbox-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="joinWaitlist"
                        checked={popupForm.joinWaitlist}
                        onChange={handlePopupInputChange}
                      />
                      <span className="checkmark"></span>
                      {t('popup.joinWaitlist')}
                    </label>
                  </div>
                  
                  <div className="popup-buttons">
                    <button type="button" className="btn-secondary" onClick={handlePopupClose}>
                      {t('popup.skip')}
                    </button>
                    <button type="submit" className="btn-primary" disabled={isSubmitting}>
                      {t('popup.joinWaitlist')}
                    </button>
                  </div>
                </form>
              </>
            ) : (
              <div className="popup-success">
                <div className="success-icon">✓</div>
                <h3>{t('popup.success')}</h3>
              </div>
            )}
          </div>
        </div>
      )}

      <LandingHeader language={language} onLanguageChange={handleLanguageChange} t={t} />

      <section className="hero-section">
        <div className="hero-grid">
          <div className="hero-content">
            <h1 className="hero-title">{highlightTextJSX(t('hero.title'))}</h1>
            <p className="hero-subtitle">
              {highlightTextJSX(t('hero.subtitle'))}
            </p>
            <div className="hero-buttons">
              <Link to="/registration" className="btn btn-primary">{t('hero.joinWaitlist')}</Link>
              <button className="btn btn-secondary">{t('hero.seeHowWorks')}</button>
            </div>
          </div>
          <div className="hero-mascot-figure">
            <LandingWelbyAnimation animation="idle" />
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="problem-section py-20">
        <div className="container">
          <div className="text-center mb-12">
            <h2>{t('problem.heading')}</h2>
            <p className="hero-subtitle" style={{maxWidth: '48rem', margin: '1rem auto 0'}}>
              {highlightTextJSX(t('problem.subheading'))}
            </p>
          </div>

          <div className="problem-grid">
            <div className="problem-card">
              <div className="problem-icon">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"></path>
                </svg>
              </div>
              <p className="problem-title">{t('problem.portals.title')}</p>
              <h3 className="problem-subtitle">{t('problem.portals.subtitle')}</h3>
              <p className="problem-description">{t('problem.portals.desc')}</p>
              <div className="problem-welby">
                <img src={welbyImg} alt="Welby" className="welby-small" />
                <p className="welby-text">{t('problem.portals.welby')}</p>
              </div>
            </div>

            <div className="problem-card">
              <div className="problem-icon">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
              </div>
              <p className="problem-title">{t('problem.instructions.title')}</p>
              <h3 className="problem-subtitle">{t('problem.instructions.subtitle')}</h3>
              <p className="problem-description">{t('problem.instructions.desc')}</p>
              <div className="problem-welby">
                <img src={welbyImg} alt="Welby" className="welby-small" />
                <p className="welby-text">{t('problem.instructions.welby')}</p>
              </div>
            </div>

            <div className="problem-card">
              <div className="problem-icon">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                </svg>
              </div>
              <p className="problem-title">{t('problem.appointments.title')}</p>
              <h3 className="problem-subtitle">{t('problem.appointments.subtitle')}</h3>
              <p className="problem-description">{t('problem.appointments.desc')}</p>
              <div className="problem-welby">
                <img src={welbyImg} alt="Welby" className="welby-small" />
                <p className="welby-text">{t('problem.appointments.welby')}</p>
              </div>
            </div>

            <div className="problem-card">
              <div className="problem-icon">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path>
                </svg>
              </div>
              <p className="problem-title">{t('problem.caregiver.title')}</p>
              <h3 className="problem-subtitle">{t('problem.caregiver.subtitle')}</h3>
              <p className="problem-description">{t('problem.caregiver.desc')}</p>
              <div className="problem-welby">
                <img src={welbyImg} alt="Welby" className="welby-small" />
                <p className="welby-text">{t('problem.caregiver.welby')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="solution-section py-20">
        <div className="container">
          <div className="solution-grid">
            <div className="solution-content">
              <div className="solution-header">
                <img src={welbyImg} alt="Welby" className="welby-medium" />
                <h2>{t('solution.welbyTitle')}</h2>
              </div>
              <p className="hero-subtitle">
                {highlightTextJSX(t('solution.welbyIntro'))}
              </p>
              <p className="solution-tagline">
                "{highlightTextJSX(t('solution.tagline'))}"
              </p>
              <div className="solution-features">
                <div className="feature-item">
                  <div className="feature-icon">
                    <svg fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
                    </svg>
                  </div>
                  <span className="feature-text">{t('solution.features.0')}</span>
                </div>
                <div className="feature-item">
                  <div className="feature-icon">
                    <svg fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
                    </svg>
                  </div>
                  <span className="feature-text">{t('solution.features.1')}</span>
                </div>
                <div className="feature-item">
                  <div className="feature-icon">
                    <svg fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
                    </svg>
                  </div>
                  <span className="feature-text">{t('solution.features.2')}</span>
                </div>
                <div className="feature-item">
                  <div className="feature-icon">
                    <svg fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
                    </svg>
                  </div>
                  <span>{t('solution.features.3')}</span>
                </div>
                <div className="feature-item">
                  <div className="feature-icon">
                    <svg fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
                    </svg>
                  </div>
                  <span className="feature-text">{t('solution.features.4')}</span>
                </div>
                <div className="feature-item">
                  <div className="feature-icon">
                    <svg fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
                    </svg>
                  </div>
                  <span className="feature-text">{t('solution.features.5')}</span>
                </div>
              </div>
            </div>

            <div className="task-list-container">
              <h3 className="task-list-title">{t('solution.tasks')}</h3>
              <div className="task-item">
                <div className="task-checkbox completed">
                  <svg fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                  </svg>
                </div>
                <span className="task-text completed">{t('solution.pickupMed')}</span>
              </div>
              <div className="task-item">
                <div className="task-checkbox completed">
                  <svg fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                  </svg>
                </div>
                <span className="task-text completed">{t('solution.scheduleFU')}</span>
              </div>
              <div className="task-item">
                <div className="task-checkbox incomplete"></div>
                <span className="task-text">{t('solution.dailyReminders')}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="how-it-works-section py-20">
        <div className="container">
          <div className="text-center mb-12">
            <h2>{t('howItWorks.heading')}</h2>
          </div>

          <div className="steps-grid">
            <div className="step-item">
              <div className="step-icon">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path>
                </svg>
              </div>
              <h3 className="step-title">{t('howItWorks.step1.title')}</h3>
              <p className="step-description">{t('howItWorks.step1.desc')}</p>
            </div>

            <div className="step-item">
              <div className="step-icon">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"></path>
                </svg>
              </div>
              <h3 className="step-title">{t('howItWorks.step2.title')}</h3>
              <p className="step-description">{t('howItWorks.step2.desc')}</p>
            </div>

            <div className="step-item">
              <div className="step-icon">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"></path>
                </svg>
              </div>
              <h3 className="step-title">{t('howItWorks.step3.title')}</h3>
              <p className="step-description">{t('howItWorks.step3.desc')}</p>
            </div>
          </div>

          <div className="text-center mt-12">
            <Link to="/registration" className="btn btn-primary">{t('hero.joinWaitlist')}</Link>
          </div>
        </div>
      </section>

      <section className="welby-demo-section py-20">
        <div className="demo-container">
          <div className="text-center mb-12">
            <h2>{t('demo.heading')}</h2>
            <p className="hero-subtitle" style={{maxWidth: '48rem', margin: '1rem auto 0'}}>
              {highlightTextJSX(t('demo.subheading'))}
            </p>
          </div>

          <div className="demo-chat-container">
            <div className="demo-presence-panel">
              <div className="demo-presence-copy">
                <span className="demo-presence-eyebrow">{t('demo.assistant')}</span>
                <h3>{t('demo.presenceTitle')}</h3>
                <p>{t('demo.presenceBody')}</p>
                <span className={`demo-status-pill demo-status-pill-standalone ${welbyLoading ? 'thinking' : ''}`}>
                  {welbyLoading ? t('demo.statusThinking') : t('demo.statusReady')}
                </span>
              </div>

              <div className="demo-stage">
                <div className="demo-mascot-animation">
                  <LandingWelbyAnimation animation={welbyLoading ? 'thinking' : 'idle'} />
                </div>
                <div className="demo-stage-shadow" />
              </div>
            </div>

            <div className="demo-interaction">
              <div className="demo-transcript-header">
                <div>
                  <span className="demo-transcript-label">{t('demo.transcriptTitle')}</span>
                  <h3>Welby</h3>
                </div>
              </div>

              <div className="demo-chat-messages">
                {conversationTrail.map((msg, index) => (
                  <div key={index} className={`demo-message-card ${msg.type}`}>
                    <div className="demo-message-label">
                      {msg.type === 'user' ? t('demo.youLabel') : t('demo.welbyLabel')}
                    </div>
                    <div className="demo-message-card-body">
                      <div
                        className="demo-message-markdown"
                        dangerouslySetInnerHTML={{
                          __html: formatMarkdownToHtml(msg.text)
                        }}
                      />
                      {msg.attachments?.length > 0 && (
                        <div className="demo-message-attachments" aria-label={t('demo.selectedFilesLabel')}>
                          {msg.attachments.map((attachment, attachmentIndex) => (
                            <span className="demo-message-attachment" key={`${attachment.name}-${attachmentIndex}`}>
                              <FileText size={14} aria-hidden="true" />
                              {attachment.name}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {welbyError && <div className="demo-error">{welbyError}</div>}

              <form className="demo-input-form" onSubmit={handleWelbySubmit}>
                <label htmlFor="welby-question" className="demo-input-label">
                  {t('demo.inputLabel')}
                </label>
                <div className="demo-assistive-controls">
                  <button
                    type="button"
                    className={`demo-tool-button ${isListening ? 'active' : ''}`}
                    onClick={handleVoiceInputToggle}
                    disabled={welbyLoading && !isListening}
                    aria-pressed={isListening}
                    title={speechInputSupported ? t('demo.voiceInput') : t('demo.voiceUnavailable')}
                  >
                    {isListening ? <MicOff size={18} aria-hidden="true" /> : <Mic size={18} aria-hidden="true" />}
                    <span>{isListening ? t('demo.voiceStop') : t('demo.voiceInput')}</span>
                  </button>

                  <button
                    type="button"
                    className={`demo-tool-button ${botVoiceEnabled ? 'active' : ''} ${isWelbySpeaking ? 'speaking' : ''}`}
                    onClick={handleBotVoiceToggle}
                    aria-pressed={botVoiceEnabled}
                    title={speechOutputSupported ? t('demo.botVoiceStyle') : t('demo.botVoiceUnavailable')}
                  >
                    {botVoiceEnabled ? <Volume2 size={18} aria-hidden="true" /> : <VolumeX size={18} aria-hidden="true" />}
                    <span>{botVoiceEnabled ? t('demo.botVoiceStyle') : t('demo.botVoiceOff')}</span>
                  </button>

                  <label className={`demo-file-button ${welbyLoading ? 'disabled' : ''}`}>
                    <Upload size={18} aria-hidden="true" />
                    <span>{t('demo.uploadButton')}</span>
                    <input
                      ref={fileInputRef}
                      className="demo-file-input"
                      type="file"
                      accept={DEMO_FILE_ACCEPT}
                      multiple
                      onChange={handleDemoFileChange}
                      disabled={welbyLoading}
                    />
                  </label>
                </div>
                <p className="demo-upload-hint">{t('demo.uploadHint')}</p>

                {selectedFiles.length > 0 && (
                  <div className="demo-file-list" aria-label={t('demo.selectedFilesLabel')}>
                    {selectedFiles.map((file, fileIndex) => (
                      <span className="demo-file-chip" key={`${file.name}-${file.size}-${file.lastModified}`}>
                        <FileText size={15} aria-hidden="true" />
                        <span className="demo-file-chip-name">{file.name}</span>
                        <span className="demo-file-chip-size">{formatDemoFileSize(file.size)}</span>
                        <button
                          type="button"
                          className="demo-file-chip-remove"
                          onClick={() => handleRemoveDemoFile(fileIndex)}
                          aria-label={`${t('demo.removeFile')}: ${file.name}`}
                        >
                          <X size={14} aria-hidden="true" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}

                <textarea
                  id="welby-question"
                  className="demo-input-field demo-input-textarea"
                  value={welbyQuery}
                  onChange={handleWelbyQueryChange}
                  placeholder={t('demo.inputPlaceholder')}
                  disabled={welbyLoading}
                  rows="3"
                />
                <button type="submit" className="btn btn-primary" disabled={welbyLoading || (!welbyQuery.trim() && selectedFiles.length === 0)}>
                  {welbyLoading ? t('demo.thinkingButton') : t('demo.askButton')}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="trust-section py-20">
        <div className="trust-container">
          <div className="trust-content">
            <div className="text-center">
              <h2>{t('trust.heading')}</h2>
            </div>

            <div style={{display: 'flex', flexDirection: 'column', gap: '1.5rem', marginTop: '3rem'}}>
              <p className="trust-text">
                {highlightTextJSX(t('trust.para1'))}
              </p>
              <p className="trust-text">
                {highlightTextJSX(t('trust.para2'))}
              </p>
            </div>

            <div className="trust-quote">
              <p>"{highlightTextJSX(t('trust.quote'))}"</p>
              <p className="trust-quote-author">{t('trust.quoteAuthor')}</p>
            </div>

            <div className="trust-mission">
              <p className="trust-mission-text">
                {highlightTextJSX(t('trust.missionTitle'))}
              </p>
              <p className="trust-mission-subtext">
                {highlightTextJSX(t('trust.missionSubtitle'))}
              </p>
            </div>
          </div>
          
          <div className="trust-image">
            <img 
              src={require("../../assets/images/eva_and_grandma.jpg")}
              alt="Woman and grandma looking at pill bottle" 
              style={{width: '100%', maxWidth: '500px', borderRadius: '8px'}}
            />
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section py-20">
        <div className="faq-container">
          <div className="text-center mb-12">
            <h2>{t('faq.heading')}</h2>
            <p className="hero-subtitle" style={{marginTop: '1rem'}}>
              {highlightTextJSX(t('faq.subheading'))}
            </p>
          </div>

          <div className="faq-list">
            {t('faq.items').map((faq, index) => (
              <div key={index} className="faq-item">
                <button className="faq-question" onClick={() => toggleFAQ(index)}>
                  <span>{faq.q}</span>
                  <svg className={`faq-chevron ${openFAQ === index ? 'open' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                  </svg>
                </button>
                <div className={`faq-answer ${openFAQ === index ? 'open' : ''}`}>
                  <p>{highlightTextJSX(faq.a)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="final-cta-section py-20">
        <div className="final-cta-content">
          <h2 className="final-cta-title">{t('cta.heading')}</h2>
          <p className="final-cta-subtitle">{t('cta.subheading')}</p>
          <p className="final-cta-incentive">{t('cta.incentive')}</p>
          <Link to="/registration" className="btn btn-peach" style={{padding: '1.25rem 2.5rem', fontSize: '1.125rem'}}>{t('hero.joinWaitlist')}</Link>
        </div>
      </section>

      <Footer />
      <WelbyFloatingChat/>
      <AccessibilityMenu />
    </div>
  );
};

export default Landing;
